import { CalendarDays, Clock3, MapPin, ShieldCheck, Ticket } from 'lucide-react'
import type { ReactNode } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import type {
  ParticipantDeadlines,
  ParticipantPackageSnapshot,
  ParticipantPerformanceOption,
} from '../types'

interface ParticipantPackageSummaryProps {
  packageSnapshot: ParticipantPackageSnapshot
  deadlines?: ParticipantDeadlines
  performances?: ParticipantPerformanceOption[]
  performanceQuantities?: Record<string, number>
  heading?: string
}

export function ParticipantPackageSummary({
  packageSnapshot,
  deadlines,
  performances = [],
  performanceQuantities = {},
  heading = 'Your participant package',
}: ParticipantPackageSummaryProps) {
  const selectedPerformances = performances.filter(
    (performance) => (performanceQuantities[performance.performanceId] ?? 0) > 0
  )

  return (
    <Card className="overflow-hidden border-primary/20 shadow-none">
      <CardContent className="space-y-5 p-5 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {heading}
            </p>
            <h2 className="mt-1 text-xl font-semibold">{packageSnapshot.ticketTypeName}</h2>
          </div>
          <div className="rounded-lg bg-primary/10 px-4 py-3 text-left sm:text-right">
            <p className="text-xs text-muted-foreground">Exact total</p>
            <p className="text-2xl font-semibold tracking-tight">
              {formatParticipantMoney(packageSnapshot.priceCents, packageSnapshot.currency)}
            </p>
            <p className="text-xs text-muted-foreground">
              {packageSnapshot.taxInclusive ? 'Tax included' : 'Inclusive total'} · No added fees
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <SummaryTile
            icon={<Ticket className="h-4 w-4" aria-hidden="true" />}
            label="Included admission"
            value={`${packageSnapshot.includedTicketQuantity} ticket${packageSnapshot.includedTicketQuantity === 1 ? '' : 's'}`}
          />
          {deadlines?.paymentDueAt && (
            <SummaryTile
              icon={<Clock3 className="h-4 w-4" aria-hidden="true" />}
              label="Payment due"
              value={formatDateTime(deadlines.paymentDueAt)}
              tone="warning"
            />
          )}
          {deadlines?.paymentExpiryAt && (
            <SummaryTile
              icon={<Clock3 className="h-4 w-4" aria-hidden="true" />}
              label="Final payment window"
              value={formatDateTime(deadlines.paymentExpiryAt)}
            />
          )}
          {deadlines?.hardCutoffAt && (
            <SummaryTile
              icon={<CalendarDays className="h-4 w-4" aria-hidden="true" />}
              label="Hard show cutoff"
              value={formatDateTime(deadlines.hardCutoffAt)}
            />
          )}
        </div>

        {selectedPerformances.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold">Reserved performances</h3>
              <Badge variant="outline">
                {selectedPerformances.reduce(
                  (total, performance) =>
                    total + (performanceQuantities[performance.performanceId] ?? 0),
                  0
                )}{' '}
                ticket{packageSnapshot.includedTicketQuantity === 1 ? '' : 's'}
              </Badge>
            </div>
            <div className="divide-y rounded-lg border">
              {selectedPerformances.map((performance) => (
                <div
                  key={performance.performanceId}
                  className="flex flex-col gap-2 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="font-medium">{performance.title}</p>
                    <p className="flex items-center gap-1.5 text-muted-foreground">
                      <CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {formatDateTime(performance.startAt)}
                    </p>
                    <p className="flex items-center gap-1.5 text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {performance.venue}
                    </p>
                  </div>
                  <Badge variant="secondary" className="self-start sm:self-center">
                    {performanceQuantities[performance.performanceId] ?? 0} ticket
                    {(performanceQuantities[performance.performanceId] ?? 0) === 1 ? '' : 's'}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-start gap-2 rounded-lg border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <p>
            Your participant package is a flat, tax-inclusive amount. Payment is final and
            non-refundable, including if you later withdraw or cannot participate. Purchased
            admission tickets remain valid.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

function SummaryTile({
  icon,
  label,
  value,
  tone = 'default',
}: {
  icon: ReactNode
  label: string
  value: string
  tone?: 'default' | 'warning'
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border bg-muted/20 p-3">
      <span
        className={tone === 'warning' ? 'mt-0.5 text-amber-600' : 'mt-0.5 text-muted-foreground'}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="mt-0.5 text-sm font-medium">{value}</p>
      </div>
    </div>
  )
}

export function formatParticipantMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100)
  } catch {
    return `${currency.toUpperCase()} ${(cents / 100).toFixed(2)}`
  }
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
